import type { EventBus } from '../core/EventBus';
import type { DebugUiToEngineCommand, EngineToDebugUiEvent, DebugHudSnapshot, LightProperties } from './types';

const UI_EVENT_NAME = 'himum:debug-ui-event';
const UI_COMMAND_NAME = 'himum:debug-ui-command';

export interface DebugUiHandlers {
  setDebugRendering(enabled: boolean): void;
  setSplineDebug(enabled: boolean): void;
  setPlayerDebug(enabled: boolean): void;
  setWeaponMountDebug(enabled: boolean): void;
  setCombatDebug(enabled: boolean): void;
  setCollisionDebug(enabled: boolean): void;
  setLightDebug(enabled: boolean): void;

  setPixelation(enabled: boolean): void;
  setPixelSize(value: number): void;
  setPixelAnimate(enabled: boolean): void;
  setGodRays(enabled: boolean): void;
  setMeshGodRaysExposure(meshName: string, value: number): void;
  setMeshGodRaysDecay(meshName: string, value: number): void;
  setMeshGodRaysWeight(meshName: string, value: number): void;
  setMeshGodRaysDensity(meshName: string, value: number): void;
  setChromaticAberration(enabled: boolean): void;
  setChromaticAberrationAmount(value: number): void;
  setChromaticAberrationRadialIntensity(value: number): void;
  
  // Lens effect handlers
  setLensEffect(enabled: boolean): void;
  setLensEdgeBlur(value: number): void;
  setLensChromaticAberration(value: number): void;
  setLensDistortion(value: number): void;
  setLensGrain(value: number): void;
  setLensDofDistance(value: number): void;
  setLensDofAperture(value: number): void;
  setLensDofGain(value: number): void;
  setLensDofAutoFocus(enabled: boolean): void;
  setLensDofEnabled(enabled: boolean): void;
  
  // Fog handlers
  setFogEnabled(enabled: boolean): void;
  setFogDensity(value: number): void;
  setFogColor(r: number, g: number, b: number): void;
  
  // Ambient color handler
  setAmbientColor(r: number, g: number, b: number): void;

  // HDR environment handlers
  loadHdr(url: string, envIntensity?: number, skyboxBrightness?: number): void;
  setEnvIntensity(value: number): void;
  setSkyboxBrightness(value: number): void;

  setFullBright(enabled: boolean): void;
  setLightGizmos(enabled: boolean): void;
  setLightHighlight(lightName: string | null): void;

  setScenePickMode(enabled: boolean): void;
  refreshSceneGraph(pinnedNodeIds: string[], selectedNodeId?: string | null): Promise<void>;
  setExplorerVisibility(visible: boolean): void;
  highlightNode(nodeId: string | null): void;
  toggleTimeline(): void;
  setTimeline(enabled: boolean): void;
  refreshEnvironment(): void;
  resetHud(): void;
  changeMap(mapPath: string): Promise<void>;
  
  getCameras(): void;
  jumpToCamera(cameraName: string): void;
  toggleCameraDebugVisibility(visible: boolean): void;
  getCharacters(): void;
  
  // Impact effects
  triggerImpactPreset(preset: string): void;
  triggerRipple(config: { centerX: number; centerY: number; amplitude?: number; duration?: number; frequency?: number; speed?: number; timeRamp?: string }): void;
  triggerShake(config: { amplitude?: number; duration?: number; frequency?: number; zoomAmount?: number; timeRamp?: string }): void;
  cancelAllImpactEffects(): void;
  
  // Light modification
  updateLight(nodeId: string, property: keyof LightProperties, value: number | boolean | { r: number; g: number; b: number }): void;
  
  // Spatial culling
  setCullingEnabled(enabled: boolean): void;
  setCullingDistance(value: number): void;
  setFrustumFarPlane(value: number): void;
  
  // Behavior editor
  refreshBehaviors(): void;
  updateBehaviorParam(entityId: string, paramName: string, value: number | string | boolean): void;
  
  // Material behavior (explorer panel)
  updateMaterialBehaviorParam(entityId: string, paramName: string, value: number | string | boolean): void;
}

export class DebugUiBridge {
  private handlers: DebugUiHandlers;
  private onCommandBound: (e: Event) => void;

  constructor(private eventBus: EventBus, handlers: DebugUiHandlers) {
    this.handlers = handlers;

    this.onCommandBound = (e: Event) => {
      const ce = e as CustomEvent<DebugUiToEngineCommand>;
      const cmd = ce.detail;
      if (!cmd) return;

      switch (cmd.type) {
        case 'debugRendering:set':
          this.handlers.setDebugRendering(cmd.enabled);
          return;
        case 'splineDebug:set':
          this.handlers.setSplineDebug(cmd.enabled);
          return;
        case 'playerDebug:set':
          this.handlers.setPlayerDebug(cmd.enabled);
          return;
        case 'weaponMountDebug:set':
          this.handlers.setWeaponMountDebug(cmd.enabled);
          return;
        case 'combatDebug:set':
          this.handlers.setCombatDebug(cmd.enabled);
          return;
        case 'collisionDebug:set':
          this.handlers.setCollisionDebug(cmd.enabled);
          return;
        case 'lightDebug:set':
          this.handlers.setLightDebug(cmd.enabled);
          return;

        case 'postprocess:pixelation:set':
          this.handlers.setPixelation(cmd.enabled);
          return;
        case 'postprocess:pixelSize:set':
          this.handlers.setPixelSize(cmd.value);
          return;
        case 'postprocess:pixelAnimate:set':
          this.handlers.setPixelAnimate(cmd.enabled);
          return;
        case 'postprocess:godRays:set':
          this.handlers.setGodRays(cmd.enabled);
          return;
        case 'postprocess:meshGodRays:exposure:set':
          this.handlers.setMeshGodRaysExposure(cmd.meshName, cmd.value);
          return;
        case 'postprocess:meshGodRays:decay:set':
          this.handlers.setMeshGodRaysDecay(cmd.meshName, cmd.value);
          return;
        case 'postprocess:meshGodRays:weight:set':
          this.handlers.setMeshGodRaysWeight(cmd.meshName, cmd.value);
          return;
        case 'postprocess:meshGodRays:density:set':
          this.handlers.setMeshGodRaysDensity(cmd.meshName, cmd.value);
          return;

        case 'postprocess:chromaticAberration:set':
          this.handlers.setChromaticAberration(cmd.enabled);
          return;

        case 'postprocess:chromaticAberration:amount:set':
          this.handlers.setChromaticAberrationAmount(cmd.value);
          return;

        case 'postprocess:chromaticAberration:radialIntensity:set':
          this.handlers.setChromaticAberrationRadialIntensity(cmd.value);
          return;

        // Lens effect commands
        case 'postprocess:lens:set':
          this.handlers.setLensEffect(cmd.enabled);
          return;
        case 'postprocess:lens:edgeBlur:set':
          this.handlers.setLensEdgeBlur(cmd.value);
          return;
        case 'postprocess:lens:chromaticAberration:set':
          this.handlers.setLensChromaticAberration(cmd.value);
          return;
        case 'postprocess:lens:distortion:set':
          this.handlers.setLensDistortion(cmd.value);
          return;
        case 'postprocess:lens:grain:set':
          this.handlers.setLensGrain(cmd.value);
          return;
        case 'postprocess:lens:dofDistance:set':
          this.handlers.setLensDofDistance(cmd.value);
          return;
        case 'postprocess:lens:dofAperture:set':
          this.handlers.setLensDofAperture(cmd.value);
          return;
        case 'postprocess:lens:dofGain:set':
          this.handlers.setLensDofGain(cmd.value);
          return;
        case 'postprocess:lens:dofAutoFocus:set':
          this.handlers.setLensDofAutoFocus(cmd.enabled);
          return;
        case 'postprocess:lens:dofEnabled:set':
          this.handlers.setLensDofEnabled(cmd.enabled);
          return;

        // Fog commands
        case 'environment:fog:set':
          this.handlers.setFogEnabled(cmd.enabled);
          return;
        case 'environment:fog:density:set':
          this.handlers.setFogDensity(cmd.value);
          return;
        case 'environment:fog:color:set':
          this.handlers.setFogColor(cmd.r, cmd.g, cmd.b);
          return;

        // Ambient color command
        case 'environment:ambient:color:set':
          this.handlers.setAmbientColor(cmd.r, cmd.g, cmd.b);
          return;

        // HDR environment commands
        case 'environment:hdr:load':
          this.handlers.loadHdr(cmd.url, cmd.envIntensity, cmd.skyboxBrightness);
          return;
        case 'environment:hdr:envIntensity:set':
          this.handlers.setEnvIntensity(cmd.value);
          return;
        case 'environment:hdr:skyboxBrightness:set':
          this.handlers.setSkyboxBrightness(cmd.value);
          return;

        case 'lighting:fullBright:set':
          this.handlers.setFullBright(cmd.enabled);
          return;

        case 'lighting:gizmos:set':
          this.handlers.setLightGizmos(cmd.enabled);
          return;

        case 'lighting:highlight:set':
          this.handlers.setLightHighlight(cmd.lightName);
          return;

        case 'scene:pickMode:set':
          this.handlers.setScenePickMode(cmd.enabled);
          return;

        case 'explorer:visibility':
          this.handlers.setExplorerVisibility(cmd.visible);
          return;

        case 'scene:graph:refresh':
          void this.handlers.refreshSceneGraph(cmd.pinnedNodeIds, cmd.selectedNodeId);
          return;

        case 'scene:highlight':
          this.handlers.highlightNode(cmd.nodeId);
          return;

        case 'timeline:toggle':
          this.handlers.toggleTimeline();
          return;

        case 'timeline:set':
          this.handlers.setTimeline(cmd.enabled);
          return;

        case 'environment:refresh':
          this.handlers.refreshEnvironment();
          return;

        case 'hud:reset':
          this.handlers.resetHud();
          return;

        case 'map:change':
          void this.handlers.changeMap(cmd.path);
          return;

        case 'cameras:get':
          this.handlers.getCameras();
          return;

        case 'camera:jumpTo':
          this.handlers.jumpToCamera(cmd.cameraName);
          return;

        case 'cameras:debugVisibility:set':
          this.handlers.toggleCameraDebugVisibility(cmd.visible);
          return;

        case 'characters:get':
          this.handlers.getCharacters();
          return;
          
        // Impact effect commands
        case 'impact:triggerPreset':
          this.handlers.triggerImpactPreset(cmd.preset);
          return;
          
        case 'impact:triggerRipple':
          this.handlers.triggerRipple(cmd.config);
          return;
          
        case 'impact:triggerShake':
          this.handlers.triggerShake(cmd.config);
          return;
          
        case 'impact:cancelAll':
          this.handlers.cancelAllImpactEffects();
          return;
          
        case 'light:update':
          this.handlers.updateLight(cmd.nodeId, cmd.property, cmd.value);
          return;
          
        // Spatial culling commands
        case 'culling:set':
          this.handlers.setCullingEnabled(cmd.enabled);
          return;
          
        case 'culling:distance:set':
          this.handlers.setCullingDistance(cmd.value);
          return;
          
        case 'culling:frustum:set':
          this.handlers.setFrustumFarPlane(cmd.value);
          return;
          
        // Behavior editor commands
        case 'behaviors:refresh':
          this.handlers.refreshBehaviors();
          return;
          
        case 'behaviors:update':
          this.handlers.updateBehaviorParam(cmd.entityId, cmd.paramName, cmd.value);
          return;
          
        // Material behavior commands (explorer panel)
        case 'material:behavior:param:set':
          this.handlers.updateMaterialBehaviorParam(cmd.entityId, cmd.paramName, cmd.value);
          return;
      }
    };

    window.addEventListener(UI_COMMAND_NAME, this.onCommandBound);

    this.eventBus.on('debug-render-toggled', (data: { enabled: boolean }) => {
      this.emit({ type: 'debugRendering:changed', enabled: data.enabled });
    });
  }

  dispose(): void {
    window.removeEventListener(UI_COMMAND_NAME, this.onCommandBound);
  }

  emit(event: EngineToDebugUiEvent): void {
    window.dispatchEvent(new CustomEvent<EngineToDebugUiEvent>(UI_EVENT_NAME, { detail: event }));
  }

  emitSnapshot(snapshot: DebugHudSnapshot): void {
    this.emit({ type: 'snapshot', snapshot });
  }

  static dispatchCommand(cmd: DebugUiToEngineCommand): void {
    window.dispatchEvent(new CustomEvent<DebugUiToEngineCommand>(UI_COMMAND_NAME, { detail: cmd }));
  }

  static get uiEventName(): string {
    return UI_EVENT_NAME;
  }
}
